import { HTMLAttributes, useEffect, useMemo, useRef, useState } from 'react';

interface HoverEvent {
  /** The type of hover event being fired. */
  type: 'hoverstart' | 'hoverend';
  /** The pointer type that triggered the hover event. */
  pointerType: 'mouse' | 'pen';
  /** The target element of the hover event. */
  target: HTMLElement;
}

export interface HoverProps {
  /** Handler that is called when a hover interaction starts. */
  onHoverStart?: (e: HoverEvent) => void;
  /** Handler that is called when a hover interaction ends. */
  onHoverEnd?: (e: HoverEvent) => void;
  /** Handler that is called when the hover state changes. */
  onHoverChange?: (isHovering: boolean) => void;
  /** Whether the hover events should be disabled. */
  isDisabled?: boolean;
}

interface HoverResult {
  /** Props to spread on the target element. */
  hoverProps: HTMLAttributes<HTMLElement>;
  isHovered: boolean;
}

/**
 * iOS fires onPointerEnter twice: once with pointerType="touch" and again with
 * pointerType="mouse". We want to ignore these emulated events so they do not
 * trigger hover behavior.
 *
 * @see https://bugs.webkit.org/show_bug.cgi?id=214609
 */
let globalIgnoreEmulatedMouseEvents = false;
let hoverCount = 0;

function setGlobalIgnoreEmulatedMouseEvents() {
  globalIgnoreEmulatedMouseEvents = true;

  /**
   * Clear globalIgnoreEmulatedMouseEvents after a short timeout. iOS fires
   * onPointerEnter with pointerType="mouse" immediately after onPointerUp and
   * before onFocus. On other devices that don't have this quirk, we don't want
   * to ignore a mouse hover sometime in the distant future because a user
   * previously touched the element.
   */
  setTimeout(() => {
    globalIgnoreEmulatedMouseEvents = false;
  }, 50);
}

function handleGlobalPointerEvent(e) {
  if (e.pointerType === 'touch') {
    setGlobalIgnoreEmulatedMouseEvents();
  }
}

function setupGlobalTouchEvents() {
  if (typeof document === 'undefined') return;

  if (typeof PointerEvent !== 'undefined') {
    document.addEventListener('pointerup', handleGlobalPointerEvent);
  } else {
    document.addEventListener('touchend', setGlobalIgnoreEmulatedMouseEvents);
  }

  hoverCount++;

  return () => {
    hoverCount--;
    if (hoverCount > 0) {
      return;
    }

    if (typeof PointerEvent !== 'undefined') {
      document.removeEventListener('pointerup', handleGlobalPointerEvent);
    } else {
      document.removeEventListener(
        'touchend',
        setGlobalIgnoreEmulatedMouseEvents
      );
    }
  };
}

/**
 * Handles pointer hover interactions for an element. Normalizes behavior across
 * browsers and platforms, and ignores emulated mouse events on touch devices.
 *
 * @example
 *   const { hoverProps, isHovered } = useHover()
 *   return <div {...hoverProps} />
 */
export function useHover(props: HoverProps): HoverResult {
  const { onHoverStart, onHoverChange, onHoverEnd, isDisabled } = props;

  const [isHovered, setHovered] = useState(false);
  const state = useRef({
    isHovered: false,
    ignoreEmulatedMouseEvents: false,
    pointerType: '',
    target: null,
  }).current;

  useEffect(setupGlobalTouchEvents, []);

  const { hoverProps, triggerHoverEnd } = useMemo(() => {
    const triggerHoverStart = (event, pointerType) => {
      state.pointerType = pointerType;
      if (
        isDisabled ||
        pointerType === 'touch' ||
        state.isHovered ||
        !event.currentTarget.contains(event.target)
      ) {
        return;
      }

      state.isHovered = true;
      const target = event.currentTarget;
      state.target = target;

      if (onHoverStart) {
        onHoverStart({
          type: 'hoverstart',
          target,
          pointerType,
        });
      }

      if (onHoverChange) {
        onHoverChange(true);
      }

      setHovered(true);
    };

    const triggerHoverEnd = (event, pointerType) => {
      state.pointerType = '';
      state.target = null;

      if (pointerType === 'touch' || !state.isHovered) return;

      state.isHovered = false;
      const target = event.currentTarget;

      if (onHoverEnd) {
        onHoverEnd({
          type: 'hoverend',
          target,
          pointerType,
        });
      }

      if (onHoverChange) {
        onHoverChange(false);
      }

      setHovered(false);
    };

    const hoverProps: HTMLAttributes<HTMLElement> = {};

    if (typeof PointerEvent !== 'undefined') {
      hoverProps.onPointerEnter = (e) => {
        if (globalIgnoreEmulatedMouseEvents && e.pointerType === 'mouse')
          return;

        triggerHoverStart(e, e.pointerType);
      };

      hoverProps.onPointerLeave = (e) => {
        if (!isDisabled && e.currentTarget.contains(e.target as HTMLElement)) {
          triggerHoverEnd(e, e.pointerType);
        }
      };
    } else {
      hoverProps.onTouchStart = () => {
        state.ignoreEmulatedMouseEvents = true;
      };

      hoverProps.onMouseEnter = (e) => {
        if (
          !state.ignoreEmulatedMouseEvents &&
          !globalIgnoreEmulatedMouseEvents
        ) {
          triggerHoverStart(e, 'mouse');
        }

        state.ignoreEmulatedMouseEvents = false;
      };

      hoverProps.onMouseLeave = (e) => {
        if (!isDisabled && e.currentTarget.contains(e.target as HTMLElement)) {
          triggerHoverEnd(e, 'mouse');
        }
      };
    }

    return { hoverProps, triggerHoverEnd };
  }, [onHoverStart, onHoverChange, onHoverEnd, isDisabled, state]);

  useEffect(() => {
    // Call the triggerHoverEnd as soon as isDisabled changes to true
    if (isDisabled) {
      triggerHoverEnd({ currentTarget: state.target }, state.pointerType);
    }
  }, [isDisabled, triggerHoverEnd, state.target, state.pointerType]);

  return {
    hoverProps,
    isHovered,
  };
}
